import type APIAddress from '~/types/APIAddress';
import type Company from '~/types/Company';
import type User from '~/types/User';

import { type Activation, type NextPaymentModalAction } from '~/reducers/user/user.reducer';
import { type Asset, type AssetType } from '~/types/Asset';
import { type Entitlements } from '~/types/Entitlements';
import { IDVerificationStatus, type IDVerification } from '~/types/IDVerification';

import { ActionKeys } from './constants';

export const updateCompany = (payload: Company) =>
  <const>{
    payload,
    type: ActionKeys.COMPANY_UPDATE,
  };

export const updateCompanySuccess = (payload: Company) =>
  <const>{
    payload,
    type: ActionKeys.COMPANY_UPDATE_SUCCESS,
  };

export const updateCompanyError = () =>
  <const>{
    type: ActionKeys.COMPANY_UPDATE_ERROR,
  };

export const fetchCompany = () =>
  <const>{
    type: ActionKeys.COMPANY_FETCH,
  };

export const fetchCompanySuccess = (payload: Company) =>
  <const>{
    payload,
    type: ActionKeys.COMPANY_FETCH_SUCCESS,
  };

export const fetchCompanyError = () =>
  <const>{
    type: ActionKeys.COMPANY_FETCH_ERROR,
  };

export const fetchUser = () =>
  <const>{
    type: ActionKeys.USER_FETCH,
  };

// TODO: Refactor to remove unnecesary type
export type FetchUserPayload = {
  user: User;
};

export const fetchUserSuccess = (payload: FetchUserPayload) =>
  <const>{
    payload,
    type: ActionKeys.USER_FETCH_SUCCESS,
  };

export const fetchUserError = () =>
  <const>{
    type: ActionKeys.USER_FETCH_ERROR,
  };

export const fetchUserEntitlements = () =>
  <const>{
    type: ActionKeys.USER_ENTITLEMENTS_FETCH,
  };

export const fetchUserEntitlementsSuccess = (payload: Entitlements) =>
  <const>{
    payload,
    type: ActionKeys.USER_ENTITLEMENTS_FETCH_SUCCESS,
  };

export const fetchUserEntitlementsError = () =>
  <const>{
    type: ActionKeys.USER_ENTITLEMENTS_FETCH_ERROR,
  };

export const updateFirstLabelPurchased = () =>
  <const>{
    type: ActionKeys.USER_FIRST_LABEL_PURCHASED,
  };

export const updateUserDefaultServiceLevel = (
  defaultProvider: string,
  defaultServiceLevel: string,
) =>
  <const>{
    defaultProvider,
    defaultServiceLevel,
    type: ActionKeys.UPDATE_USER_DEFAULT_SERVICE_LEVEL,
  };

export const updateUserDefaultReturnServiceLevel = (
  defaultReturnProvider: string,
  defaultReturnServiceLevel: string,
) =>
  <const>{
    defaultReturnProvider,
    defaultReturnServiceLevel,
    type: ActionKeys.UPDATE_USER_DEFAULT_RETURN_SERVICE_LEVEL,
  };

// TODO: Refactor to remove unnecesary type
type UpdateDefaultAddressPayload = {
  address: APIAddress;
};

export const updateUserDefaultAddress = (payload: UpdateDefaultAddressPayload) =>
  <const>{
    payload,
    type: ActionKeys.UPDATE_DEFAULT_ADDRESS,
  };

export const updateUserDefaultReturnAddress = (payload: UpdateDefaultAddressPayload) =>
  <const>{
    payload,
    type: ActionKeys.UPDATE_DEFAULT_RETURN_ADDRESS,
  };

export const revokeAuth = () =>
  <const>{
    type: ActionKeys.REVOKE_AUTH,
  };

export const userSetUnauthenticated = () =>
  <const>{
    type: ActionKeys.USER_SET_UNAUTHENTICATED,
  };

export const refreshUser = () =>
  <const>{
    type: ActionKeys.USER_REFRESH,
  };

export const updateCreditCardSuccess = () =>
  <const>{
    type: ActionKeys.USER_UPDATE_CREDIT_CARD_SUCCESS,
  };

export const fetchAssets = () =>
  <const>{
    type: ActionKeys.USER_ASSETS_FETCH,
  };

export const fetchAssetsSuccess = (assets: Asset[]) =>
  <const>{
    payload: {
      assets,
    },
    type: ActionKeys.USER_ASSETS_FETCH_SUCCESS,
  };

export const fetchAssetsError = (errorCode: string) =>
  <const>{
    payload: {
      errorCode,
    },
    type: ActionKeys.USER_ASSETS_FETCH_ERROR,
  };

export const uploadAssetDraft = (
  asset: Maybe<Asset>,
  assetType: AssetType,
  rank: number,
  file: File,
) =>
  <const>{
    payload: {
      asset,
      assetType,
      file,
      rank,
    },
    type: ActionKeys.USER_ASSET_UPLOAD_DRAFT,
  };

export const uploadAssetDraftSuccess = (assetType: AssetType, rank: number) =>
  <const>{
    payload: {
      assetType,
      rank,
    },
    type: ActionKeys.USER_ASSET_UPLOAD_DRAFT_SUCCESS,
  };

export const uploadAssetDraftError = (assetType: AssetType, rank: number, errorCode: string) =>
  <const>{
    payload: {
      assetType,
      errorCode,
      rank,
    },
    type: ActionKeys.USER_ASSET_UPLOAD_DRAFT_ERROR,
  };

export const clearAssetDraft = (
  asset: OptionalObjectId<Asset>,
  assetType: AssetType,
  rank: number,
) =>
  <const>{
    payload: {
      asset,
      assetType,
      rank,
    },
    type: ActionKeys.USER_ASSET_CLEAR_DRAFT,
  };

export const clearAssetDraftSuccess = (assetType: AssetType, rank: number) =>
  <const>{
    payload: {
      assetType,
      rank,
    },
    type: ActionKeys.USER_ASSET_CLEAR_DRAFT_SUCCESS,
  };

export const clearAssetDraftError = (assetType: AssetType, rank: number, errorCode: string) =>
  <const>{
    payload: {
      assetType,
      errorCode,
      rank,
    },
    type: ActionKeys.USER_ASSET_CLEAR_DRAFT_ERROR,
  };

export const revertAssetDraft = (
  asset: OptionalObjectId<Asset>,
  assetType: AssetType,
  rank: number,
) =>
  <const>{
    payload: {
      asset,
      assetType,
      rank,
    },
    type: ActionKeys.USER_ASSET_REVERT_DRAFT,
  };

export const revertAssetDraftSuccess = (assetType: AssetType, rank: number) =>
  <const>{
    payload: {
      assetType,
      rank,
    },
    type: ActionKeys.USER_ASSET_REVERT_DRAFT_SUCCESS,
  };

export const revertAssetDraftError = (assetType: AssetType, rank: number, errorCode: string) =>
  <const>{
    payload: {
      assetType,
      errorCode,
      rank,
    },
    type: ActionKeys.USER_ASSET_REVERT_DRAFT_ERROR,
  };

export const publishAsset = (asset: OptionalObjectId<Asset>, assetType: AssetType, rank: number) =>
  <const>{
    payload: {
      asset,
      assetType,
      rank,
    },
    type: ActionKeys.USER_ASSET_PUBLISH,
  };

export const publishAssetSuccess = (assetType: AssetType, rank: number) =>
  <const>{
    payload: {
      assetType,
      rank,
    },
    type: ActionKeys.USER_ASSET_PUBLISH_SUCCESS,
  };

export const publishAssetError = (assetType: AssetType, rank: number, errorCode: string) =>
  <const>{
    payload: {
      assetType,
      errorCode,
      rank,
    },
    type: ActionKeys.USER_ASSET_PUBLISH_ERROR,
  };

export const uploadAndPublishLogo = (asset: Maybe<Asset>, file: File) =>
  <const>{
    payload: {
      asset,
      file,
    },
    type: ActionKeys.USER_ASSET_UPLOAD_AND_PUBLISH_LOGO,
  };

export const uploadAndPublishLogoSuccess = () =>
  <const>{
    type: ActionKeys.USER_ASSET_UPLOAD_AND_PUBLISH_LOGO_SUCCESS,
  };

export const uploadAndPublishLogoError = (errorCode: string) =>
  <const>{
    payload: {
      errorCode,
    },
    type: ActionKeys.USER_ASSET_UPLOAD_AND_PUBLISH_LOGO_ERROR,
  };

export const updateBrandColors = (brandPrimaryColor: string, brandSecondaryColor: string) =>
  <const>{
    payload: {
      brandPrimaryColor,
      brandSecondaryColor,
    },
    type: ActionKeys.USER_BRAND_UPDATE_COLORS,
  };

export const updateBrandColorsSuccess = () =>
  <const>{
    type: ActionKeys.USER_BRAND_UPDATE_COLORS_SUCCESS,
  };

export const updateBrandColorsError = () =>
  <const>{
    type: ActionKeys.USER_BRAND_UPDATE_COLORS_ERROR,
  };

export const setIdVerificationStatus = (idVerification: IDVerification) =>
  <const>{
    payload: { idVerification },
    type: ActionKeys.USER_SET_VERIFICATION_STATUS,
  };

export const setVerificationSubmitted = () =>
  setIdVerificationStatus({
    errors: [],
    state: IDVerificationStatus.VERIFICATION_SUBMITTED,
  });

export const closeVerificationModal = () =>
  <const>{
    type: ActionKeys.USER_CLOSE_VERIFICATION_MODAL,
  };

export const initiateVerification = (returnUrl?: string) =>
  <const>{
    payload: { returnUrl },
    type: ActionKeys.USER_INITIATE_ID_VERIFICATION,
  };

export const updateActivationData = (activation: Activation) =>
  <const>{
    payload: activation,
    type: ActionKeys.UPDATE_ACTIVATION_DATA,
  };

export const showPromoApplicationNotice = () =>
  <const>{
    type: ActionKeys.SHOW_PROMO_APPLICATION_NOTICE,
  };

export const dismissPromoApplicationNotice = () =>
  <const>{
    type: ActionKeys.DISMISS_PROMO_APPLICATION_NOTICE,
  };

export const addUser = (email, callback) =>
  <const>{
    callback,
    payload: {
      email,
    },
    type: ActionKeys.USER_ADD,
  };

export const getLoginUsers = (callback) =>
  <const>{
    callback,
    type: ActionKeys.USER_ALL_FETCH,
  };

export const resendInvite = (callback, id) =>
  <const>{
    callback,
    payload: {
      userId: id,
    },
    type: ActionKeys.USER_RESEND_INVITE,
  };

export const deleteUser = (callback, id) =>
  <const>{
    callback,
    payload: {
      userId: id,
    },
    type: ActionKeys.USER_DELETE,
  };

export const maybeShowEmailVerificationModal = (nextModalAction: NextPaymentModalAction) =>
  <const>{
    payload: {
      nextModalAction,
    },
    type: ActionKeys.MAYBE_SHOW_EMAIL_VERIFICATION_MODAL,
  };

export const showEmailVerificationModal = (nextModalAction: NextPaymentModalAction) =>
  <const>{
    payload: {
      nextModalAction,
    },
    type: ActionKeys.SHOW_EMAIL_VERIFICATION_MODAL,
  };

export const closeEmailVerificationModal = () =>
  <const>{
    type: ActionKeys.CLOSE_EMAIL_VERIFICATION_MODAL,
  };

export const emailVerificationSuccess = () =>
  <const>{
    type: ActionKeys.USER_EMAIL_VERIFICATION_SUCCESS,
  };

export const checkRequiresEmailVerification = (verifySuccessCallback: () => void = () => {}) =>
  <const>{
    payload: {
      verifySuccessCallback,
    },
    type: ActionKeys.CHECK_REQUIRES_EMAIL_VERIFICATION,
  };

export type UserActions =
  | ReturnType<typeof addUser>
  | ReturnType<typeof clearAssetDraft>
  | ReturnType<typeof clearAssetDraftError>
  | ReturnType<typeof clearAssetDraftSuccess>
  | ReturnType<typeof closeEmailVerificationModal>
  | ReturnType<typeof closeVerificationModal>
  | ReturnType<typeof deleteUser>
  | ReturnType<typeof dismissPromoApplicationNotice>
  | ReturnType<typeof emailVerificationSuccess>
  | ReturnType<typeof fetchAssets>
  | ReturnType<typeof fetchAssetsError>
  | ReturnType<typeof fetchAssetsSuccess>
  | ReturnType<typeof fetchCompany>
  | ReturnType<typeof fetchCompanyError>
  | ReturnType<typeof fetchCompanySuccess>
  | ReturnType<typeof fetchUser>
  | ReturnType<typeof fetchUserEntitlements>
  | ReturnType<typeof fetchUserEntitlementsError>
  | ReturnType<typeof fetchUserEntitlementsSuccess>
  | ReturnType<typeof fetchUserError>
  | ReturnType<typeof fetchUserSuccess>
  | ReturnType<typeof getLoginUsers>
  | ReturnType<typeof initiateVerification>
  | ReturnType<typeof maybeShowEmailVerificationModal>
  | ReturnType<typeof publishAsset>
  | ReturnType<typeof publishAssetError>
  | ReturnType<typeof publishAssetSuccess>
  | ReturnType<typeof refreshUser>
  | ReturnType<typeof resendInvite>
  | ReturnType<typeof revertAssetDraft>
  | ReturnType<typeof revertAssetDraftError>
  | ReturnType<typeof revertAssetDraftSuccess>
  | ReturnType<typeof revokeAuth>
  | ReturnType<typeof setIdVerificationStatus>
  | ReturnType<typeof showEmailVerificationModal>
  | ReturnType<typeof showPromoApplicationNotice>
  | ReturnType<typeof updateActivationData>
  | ReturnType<typeof updateBrandColors>
  | ReturnType<typeof updateBrandColorsError>
  | ReturnType<typeof updateBrandColorsSuccess>
  | ReturnType<typeof updateCompany>
  | ReturnType<typeof updateCompanyError>
  | ReturnType<typeof updateCompanySuccess>
  | ReturnType<typeof updateCreditCardSuccess>
  | ReturnType<typeof updateFirstLabelPurchased>
  | ReturnType<typeof updateUserDefaultAddress>
  | ReturnType<typeof updateUserDefaultReturnAddress>
  | ReturnType<typeof updateUserDefaultReturnServiceLevel>
  | ReturnType<typeof updateUserDefaultServiceLevel>
  | ReturnType<typeof uploadAndPublishLogo>
  | ReturnType<typeof uploadAndPublishLogoError>
  | ReturnType<typeof uploadAndPublishLogoSuccess>
  | ReturnType<typeof uploadAssetDraft>
  | ReturnType<typeof uploadAssetDraftError>
  | ReturnType<typeof uploadAssetDraftSuccess>
  | ReturnType<typeof userSetUnauthenticated>;
